import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../config/api';
import { isEmployer } from '../utils/userRole';
import useLiveRefresh from '../hooks/useLiveRefresh';
import './GroupsFeedSidebar.css';

function resolveNewsThumb(item) {
  const mediaList = Array.isArray(item?.media) ? item.media : [];
  if (!mediaList.length) return { url: '', isVideo: false };

  const image = mediaList.find((m) => {
    const type = String(m.type || '').toLowerCase();
    const url = String(m.url || '');
    if (type === 'video') return false;
    if (/\.(mp4|webm|mov|m4v)(\?|$)/i.test(url)) return false;
    return Boolean(url);
  });

  if (image?.url) return { url: image.url, isVideo: false };

  const video = mediaList.find((m) => m.url);
  if (video?.url) return { url: video.url, isVideo: true };

  return { url: '', isVideo: false };
}

function resolveEventThumb(event, apiUrl) {
  if (event.thumbnail_url) return event.thumbnail_url;
  if (event.media_url) {
    const url = String(event.media_url);
    const isVideo =
      event.media_type === 'video' ||
      /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url) ||
      /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
    if (!isVideo) return url;
  }
  if (event.thumbnail) {
    const t = String(event.thumbnail);
    if (/^https?:\/\//i.test(t)) return t;
    return `${apiUrl}/uploads/events/${t.replace(/^\/+/, '')}`;
  }
  if (event.media && event.media_type !== 'video') {
    const m = String(event.media);
    if (/^https?:\/\//i.test(m)) return m;
    if (!/\.(mp4|webm|mov|m4v)(\?|$)/i.test(m)) {
      return `${apiUrl}/uploads/events/${m.replace(/^\/+/, '')}`;
    }
  }
  return '';
}

function isVideoEvent(event) {
  if (event.media_type === 'video') return true;
  const url = String(event.media_url || event.media || '');
  return (
    /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url) ||
    /youtube\.com|youtu\.be|vimeo\.com/i.test(url)
  );
}

function SeeMoreChevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function SectionHeader({ title, to, linkLabel = 'See more', linkClass = 'gfs-see-more' }) {
  return (
    <div className="gfs-subheader">
      <h3 className="gfs-subtitle">{title}</h3>
      <Link to={to} className={linkClass}>
        {linkLabel}
        {linkClass === 'gfs-see-more' ? <SeeMoreChevron /> : null}
      </Link>
    </div>
  );
}

/**
 * Combined home-rail panel: News & Highlights, Series of Events, Attend Events.
 */
export default function DiscoverFeedSidebar({ user }) {
  const API_URL = getApiBaseUrl();
  const [news, setNews] = useState([]);
  const [seriesEvents, setSeriesEvents] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (options = {}) => {
      const silent = options.silent === true;
      if (!silent) setLoading(true);
      try {
        const attendanceQ = user?.id
          ? `?user_id=${user.id}&limit=50`
          : '?limit=50';
        const [newsRes, eventsRes, attendanceRes] = await Promise.all([
          fetch(`${API_URL}/get_newslist.php?limit=12`),
          fetch(`${API_URL}/get_event_activities.php`),
          fetch(`${API_URL}/get_attendance_events.php${attendanceQ}`),
        ]);

        const newsData = await newsRes.json();
        if (newsData.success && Array.isArray(newsData.news)) {
          const unique = Array.from(
            new Map(newsData.news.map((item) => [item.id, item])).values()
          );
          unique.sort(
            (a, b) =>
              new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
          setNews(unique.slice(0, 1));
        } else if (!silent) {
          setNews([]);
        }

        const eventsData = await eventsRes.json();
        if (eventsData.success && Array.isArray(eventsData.data)) {
          setSeriesEvents(eventsData.data.slice(0, 1));
        } else if (!silent) {
          setSeriesEvents([]);
        }

        const attendanceData = await attendanceRes.json();
        const list =
          attendanceData.success && Array.isArray(attendanceData.events)
            ? attendanceData.events
            : [];
        list.sort((a, b) => {
          const ta = Date.parse(`${a.event_date || ''}T00:00:00`) || 0;
          const tb = Date.parse(`${b.event_date || ''}T00:00:00`) || 0;
          if (tb !== ta) return tb - ta;
          return Number(b.id || 0) - Number(a.id || 0);
        });
        setAttendance(list.slice(0, 1));
      } catch (err) {
        console.error('Discover sidebar load error:', err);
        if (!silent) {
          setNews([]);
          setSeriesEvents([]);
          setAttendance([]);
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [API_URL, user?.id]
  );

  useEffect(() => {
    load();
  }, [load]);

  useLiveRefresh(() => load({ silent: true }), 20000, true);

  return (
    <aside className="gfs-panel gfs-panel--discover">
      <div className="gfs-header">
        <h2 className="gfs-title">Discover</h2>
      </div>

      <div className="gfs-panel-scroll">
      {/* News & Highlights */}
      <SectionHeader title="News & Highlights" to="/news" />
      <div className="gfs-list gfs-list--events gfs-list--compact">
        {loading ? (
          <p className="gfs-empty">Loading…</p>
        ) : news.length === 0 ? (
          <p className="gfs-empty">No news yet.</p>
        ) : (
          news.map((item) => {
            const dateLabel = item.created_at
              ? new Date(item.created_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })
              : '';
            const category = item.category || 'Announcement';
            const { url: thumb, isVideo } = resolveNewsThumb(item);
            const showAsImage = thumb && !isVideo;

            return (
              <Link key={`news-${item.id}`} to={`/news/${item.id}`} className="gfs-item">
                <span
                  className={`gfs-thumb${isVideo && !showAsImage ? ' gfs-thumb--video' : ''}`}
                >
                  {showAsImage ? (
                    <img src={thumb} alt="" />
                  ) : (
                    <span className="gfs-thumb-play" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  )}
                </span>
                <div className="gfs-info">
                  <span className="gfs-name">{item.title || 'Untitled'}</span>
                  <span className="gfs-meta">
                    {[category, dateLabel].filter(Boolean).join(' • ')}
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Series of Events */}
      <SectionHeader title="Series of Events" to="/events" />
      <div className="gfs-list gfs-list--events gfs-list--compact">
        {loading ? (
          <p className="gfs-empty">Loading…</p>
        ) : seriesEvents.length === 0 ? (
          <p className="gfs-empty">No events yet.</p>
        ) : (
          seriesEvents.map((event) => {
            const dateLabel = event.event_date
              ? new Date(event.event_date).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })
              : '';
            const place = event.location || event.country || '';
            const thumb = resolveEventThumb(event, API_URL);
            const video = isVideoEvent(event);

            return (
              <Link
                key={`series-${event.id}`}
                to={`/events/${event.id}`}
                className="gfs-item"
              >
                <span className={`gfs-thumb${video && !thumb ? ' gfs-thumb--video' : ''}`}>
                  {thumb ? (
                    <img src={thumb} alt="" />
                  ) : (
                    <span className="gfs-thumb-play" aria-hidden="true">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  )}
                  {video && thumb && (
                    <span
                      className="gfs-thumb-play gfs-thumb-play--overlay"
                      aria-hidden="true"
                    >
                      <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </span>
                  )}
                </span>
                <div className="gfs-info">
                  <span className="gfs-name">
                    {event.event_name || 'Untitled event'}
                  </span>
                  <span className="gfs-meta">
                    {[dateLabel, place].filter(Boolean).join(' • ')}
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>

      {/* Attend Events */}
      <SectionHeader
        title="Attend Events"
        to="/event-attendance"
        linkLabel={isEmployer(user) ? 'Manage' : 'See more'}
        linkClass={isEmployer(user) ? 'gfs-manage' : 'gfs-see-more'}
      />
      <div className="gfs-list gfs-list--events gfs-list--compact gfs-list--last">
        {loading ? (
          <p className="gfs-empty">Loading…</p>
        ) : attendance.length === 0 ? (
          <p className="gfs-empty">No attendance events yet.</p>
        ) : (
          attendance.map((event) => {
            const dateLabel = event.event_date
              ? new Date(`${event.event_date}T00:00:00`).toLocaleDateString(
                  undefined,
                  { month: 'short', day: 'numeric' }
                )
              : '';
            const place = event.location || event.country || '';

            return (
              <Link
                key={`attend-${event.id}`}
                to={`/event-attendance/${event.id}`}
                className="gfs-item"
              >
                <span className="gfs-thumb">
                  {event.cover_image ? (
                    <img
                      src={
                        /^https?:\/\//i.test(event.cover_image)
                          ? event.cover_image
                          : `${API_URL}/${String(event.cover_image).replace(/^\/+/, '')}`
                      }
                      alt=""
                    />
                  ) : (
                    <span className="gfs-thumb-play" aria-hidden="true">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        width="18"
                        height="18"
                      >
                        <rect x="3" y="5" width="18" height="14" rx="2" />
                        <path d="M3 10h18" />
                      </svg>
                    </span>
                  )}
                </span>
                <div className="gfs-info">
                  <span className="gfs-name">{event.title || 'Untitled event'}</span>
                  <span className="gfs-meta">
                    {[dateLabel, place].filter(Boolean).join(' • ')}
                    {event.is_attending ? ' • Going' : ''}
                  </span>
                </div>
              </Link>
            );
          })
        )}
      </div>
      </div>
    </aside>
  );
}
